import { Box, chakra, Container, Divider, Text } from '@chakra-ui/react';
import { AxiosError } from 'axios';
import { useRouter } from 'next/router';
import { signIn } from 'next-auth/react';
import { useEffect, useRef, useState } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import { useHookForm } from '@/hooks/useHookForm';

import { RegisterAgreementsNoCredentials } from '@/components/Register/RegistrationAgreements/RegisterAgreementsNoCredentials';
import { Button } from '@/components/UI/Buttons/Button';

import RoutePath from '@/constants/route-path';
import { RegistrationFormError } from '@/models/api';
import { RegisterForm } from '@/models/forms/RegisterForm';
import { AppService, RegistrationService } from '@/services';
import { StringUtil } from '@/utils/StringUtil';

import { PersonalData, RegisterFailed } from './index';
import { RegisterUserFormProps } from './RegisterUserFormProps';
import { useShoppingCart } from '../../../context/shoppingCartContext';

export const RegisterNoAccount = (props: RegisterUserFormProps) => {
    const registerForm = useHookForm<RegisterForm>();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submittingErrors, setSubmittingErrors] = useState<RegistrationFormError[]>([]);
    const { handleSubmit } = registerForm;
    const { cartItemsUnlogged } = useShoppingCart();
    const router = useRouter();
    const registerFailedRef = useRef<HTMLFormElement>(null);
    const prepareRegistrationPayload = (data: RegisterForm) => {
        delete data.confirmPassword;
        delete data.confirmEmail;
        delete data.adult;
        for (const key in data) {
            const value = data[key as keyof RegisterForm];

            if (value === '' || value === null || value === undefined) {
                delete data[key as keyof RegisterForm];
            }
        }
        if (data.phone) {
            data.phone = AppService.getPhonePrefix().concat(' ').concat(data.phone);
            data.phone = StringUtil.removeSpaces(data.phone);
        }

        if (data.phone === '' || data.phone === null || data.phone === undefined) {
            delete data.phone;
        }

        data.countryCode = AppService.getAppCountryCode();

        if (data.sponsorNo) {
            data.sponsorNo = AppService.getPrefixedAccountNumber(data.sponsorNo);
        }
    };
    const onRegisterSubmit: SubmitHandler<RegisterForm> = (data) => {
        setIsSubmitting(() => true);
        setSubmittingErrors(() => []);
        prepareRegistrationPayload(data);

        RegistrationService.register(data)
            .then(async (response) => {
                await signIn('credentials', {
                    memberNumber: response.representativeNo,
                    password: '',
                    j_transparent: true,
                    redirect: false
                }).then(async (res) => {
                    if (!res) {
                        return;
                    }
                    const { ok, error } = res;

                    if (error || !ok) {
                        console.error('Sign in error:', error, res); // eslint-disable-line no-console
                        return;
                    }

                    if (!response?.hasCredentials) {
                        if (cartItemsUnlogged.length > 0) {
                            return router.push(
                                `${RoutePath.AddToCart}?redirectURL=${RoutePath.Checkout}`
                            );
                        }

                        return router.push(RoutePath.Products);
                    }
                });

                if (props?.onRegisterSubmit) {
                    props.onRegisterSubmit(response);
                }
            })
            .catch((error: AxiosError<{ errors: RegistrationFormError[] }>) => {
                const errors = error.response?.data?.errors || [];
                setSubmittingErrors(() => errors);
            })
            .finally(() => {
                setIsSubmitting(() => false);
            });
    };

    useEffect(() => {
        if (!submittingErrors.length) {
            return;
        }
        registerFailedRef.current?.scrollIntoView({
            behavior: 'smooth'
        });
    }, [submittingErrors]);

    return (
        <chakra.form ref={registerFailedRef} noValidate onSubmit={handleSubmit(onRegisterSubmit)}>
            <Container maxW="5xl" padding={6} borderWidth={1} borderColor="grey.border">
                {!!submittingErrors.length && <RegisterFailed errors={submittingErrors} />}
                <PersonalData
                    formInstance={registerForm}
                    confirmEmail={false}
                    errorsField={submittingErrors}
                />
                <Divider orientation="horizontal" my="1.5rem" />
                <RegisterAgreementsNoCredentials formInstance={registerForm} />
                <Box textAlign="center">
                    <Button type="submit" isLoading={isSubmitting}>
                        <FormattedMessage id="go-to-checkout" />
                    </Button>
                    <Text fontSize="xs" marginTop={4}>
                        <FormattedMessage id="page.register.required-fields-info" />
                    </Text>
                </Box>
            </Container>
        </chakra.form>
    );
};

export default RegisterNoAccount;
